import React from 'react';
import { Card } from '../components/ui/display/Card.jsx';
import { Button } from '../components/ui/actions/Button.jsx';
import { CheckCircle2, Calendar, MessageSquare } from 'lucide-react';
import { tFor } from '../i18n/i18n.js';
import { formatBaht } from '../lib/price.js';

// Final confirmation. Renders the booking summary with add-to-calendar
// and a copy-cancel-link helper.

export function StepDone({ booking, service, locale, fallbackTotal }) {
  const t = tFor(locale);
  if (!booking) {
    return (
      <Card>
        <div className="sp-empty">{t('common.loading')}</div>
      </Card>
    );
  }

  // Prefer the server-issued total (authoritative — it knows about any
  // server-side adjustments), but fall back to the client-computed total
  // whenever the server value is missing or non-positive. The widget
  // computes its own total from the service + extras on the details
  // step, so we always have a value to fall back on. Without this
  // guard, a server returning `0` (e.g. a stale booking row from
  // before the price column was populated, or a future
  // server-side edit that hasn't propagated yet) would show "฿0"
  // on the thank-you page even though the customer paid a
  // different amount.
  const serverTotal = Number(booking.totalPrice);
  const hasServerTotal =
    Number.isFinite(serverTotal) && serverTotal > 0;
  const displayTotal = hasServerTotal ? serverTotal : fallbackTotal;

  const ics = buildIcsDataUri(booking, service);
  return (
    <Card>
      <div className="sp-done">
        <div className="sp-done__icon">
          <CheckCircle2 size={32} strokeWidth={2} />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 'var(--text-h2)' }}>
          {t('done.heading')}
        </h2>
        <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
          {t('done.summary').replace('{id}', String(booking.id))}
        </div>

        {booking.bookingCode && (
          <div
            style={{
              marginBottom: 16,
              padding: '14px 16px',
              border: '1px solid var(--brand)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--brand-tint)',
            }}
          >
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-brand)' }}>
              {t('done.backupCode')}
            </div>
            <output
              aria-label={t('done.backupCode')}
              style={{
                display: 'block',
                margin: '6px 0 4px',
                color: 'var(--text-body)',
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: '0.16em',
                lineHeight: 1,
              }}
            >
              {booking.bookingCode}
            </output>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              {t('done.backupCodeHint')}
            </div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: 16,
            background: 'var(--bg)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            marginBottom: 16,
            textAlign: 'left',
          }}
        >
          <Row
            label="When"
            value={`${booking.slotDate} · ${formatTime(booking.slotStart)} – ${formatTime(booking.slotEnd)}`}
          />
          <Row
            label="Total"
            value={`${formatBaht(displayTotal)} ${booking.currency}`}
          />
          {service && <Row label="Service" value={service} />}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            icon={<Calendar size={16} strokeWidth={2} />}
            onClick={() => downloadIcs(ics, `sanookpass-booking-${booking.id}.ics`)}
          >
            {t('done.calendar')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function formatTime(time) {
  return time.slice(0, 5);
}

function buildIcsDataUri(booking, service) {
  const start = booking.slotDate.replace(/-/g, '');
  const startTime = booking.slotStart.replace(':', '') + '00';
  const endTime = booking.slotEnd.replace(':', '') + '00';
  const uid = `sanookpass-${booking.id}@sanookpass.com`;
  const summary = service
    ? `${service} — SanookPass`
    : `SanookPass booking #${booking.id}`;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SanookPass//Booking//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${start}T${startTime}`,
    `DTSTART:${start}T${startTime}`,
    `DTEND:${start}T${endTime}`,
    `SUMMARY:${escapeIcs(summary)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(ics);
}

function escapeIcs(s) {
  return s.replace(/[,;\\]/g, (m) => '\\' + m);
}

function downloadIcs(dataUri, filename) {
  const a = document.createElement('a');
  a.href = dataUri;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
